import type { BillingStatus, LicenceTier } from "@/types";

/**
 * What state a licence is in, and what that costs.
 *
 * The states are not decoration. **Expiry never deletes data** — an institution
 * whose card failed keeps every scene it wrote and every statistic it earned —
 * and saying so is the first thing anybody reading a red banner needs, because
 * it is the first thing they fear.
 */

export type LicenceState = "active" | "expiring" | "paymentFailed" | "expired" | "manual";

/** Fourteen days: long enough to reach whoever signs the invoice. */
export const EXPIRY_WARNING_DAYS = 14;

export function daysUntil(validUntil: string, now: number): number {
  const expiry = Date.parse(validUntil);
  if (!Number.isFinite(expiry)) return Number.NaN;

  const startOfDay = (ms: number) => {
    const date = new Date(ms);
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  };
  return Math.round((startOfDay(expiry) - startOfDay(now)) / 86_400_000);
}

export function licenceState(status: BillingStatus, now: number): LicenceState {
  const days = daysUntil(status.validUntil, now);

  // Expiry comes first whatever else is true: an expired licence blocks new
  // lessons, and a payment-failed banner on top of it would bury the reason.
  if (Number.isFinite(days) && days < 0) return "expired";
  if (status.paymentFailedAt) return "paymentFailed";
  if (status.manualBilling) return "manual";
  if (Number.isFinite(days) && days <= EXPIRY_WARNING_DAYS) return "expiring";
  return "active";
}

export const LICENCE_BANNER: Record<
  LicenceState,
  { titleKey: string; bodyKey: string; tone: "neutral" | "warn" | "stop" } | undefined
> = {
  active: undefined,
  manual: {
    titleKey: "billing.state.manual.title",
    bodyKey: "billing.state.manual.body",
    tone: "neutral",
  },
  expiring: {
    titleKey: "billing.state.expiring.title",
    bodyKey: "billing.state.expiring.body",
    tone: "warn",
  },
  paymentFailed: {
    titleKey: "billing.state.paymentFailed.title",
    bodyKey: "billing.state.paymentFailed.body",
    tone: "warn",
  },
  expired: {
    titleKey: "billing.state.expired.title",
    bodyKey: "billing.state.expired.body",
    tone: "stop",
  },
};

/** Teacher counts per tier. Mirrors the backend, which is the enforcement. */
export const TIER_MAX_TEACHERS: Record<LicenceTier, number> = {
  solo: 1,
  small: 3,
  medium: 10,
  large: 40,
};

export const TIER_ORDER: readonly LicenceTier[] = ["solo", "small", "medium", "large"];

export type TierChange =
  | { allowed: true; direction: "upgrade" | "downgrade" | "same" }
  | { allowed: false; reason: "tooManyTeachers"; used: number; max: number };

/**
 * Whether an institution may move to a tier.
 *
 * A downgrade below the seats already in use is refused **with the numbers**,
 * because "you cannot do that" leaves an owner to work out what would let them.
 * "You have seven teachers; Small allows three" tells them exactly what to do
 * next, and it is the same sentence the backend refuses with.
 */
export function tierChange(
  from: LicenceTier,
  to: LicenceTier,
  teachersInUse: number
): TierChange {
  const max = TIER_MAX_TEACHERS[to];
  if (teachersInUse > max) {
    return { allowed: false, reason: "tooManyTeachers", used: teachersInUse, max };
  }

  const fromIndex = TIER_ORDER.indexOf(from);
  const toIndex = TIER_ORDER.indexOf(to);
  return {
    allowed: true,
    direction: toIndex === fromIndex ? "same" : toIndex > fromIndex ? "upgrade" : "downgrade",
  };
}

/**
 * Whether the self-serve controls belong on screen at all.
 *
 * An institution billed by invoice has a contract and a person; offering it a
 * checkout button would produce a second subscription nobody wanted and an
 * awkward conversation about a refund.
 */
export function showsSelfServe(status: BillingStatus): boolean {
  return !status.manualBilling;
}
